import type { CSSProperties, ReactNode, SyntheticEvent } from 'react';
import type { CoverFocalPoint, CoverFont } from '../lib/api';
import { coverFocalPointStyle } from '../lib/coverFocalPoint';
import { coverFontVariables, normalizeCoverFont } from '../lib/coverFonts';
import { formatDateOnlyLong } from '../lib/dateOnly';
import { normalizeGalleryStyle, type GalleryStyle } from '../lib/galleryCustomisation';

export interface GalleryCoverProps {
  title: string;
  description?: string;
  eventDate?: string | null;
  coverFont?: CoverFont;
  galleryStyle?: GalleryStyle;
  state: 'locked' | 'unlocked';
  imageUrl?: string;
  focalPoint?: CoverFocalPoint;
  video?: ReactNode;
  action: ReactNode;
  controls?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onImageLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onImageError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

export function GalleryCover({
  title,
  description,
  eventDate,
  coverFont,
  galleryStyle,
  state,
  imageUrl,
  focalPoint,
  video,
  action,
  controls,
  className = '',
  style,
  onImageLoad,
  onImageError
}: GalleryCoverProps) {
  const normalizedFont = normalizeCoverFont(coverFont);
  const normalizedStyle = normalizeGalleryStyle(galleryStyle);
  const unlocked = state === 'unlocked';
  const focalStyle = coverFocalPointStyle(focalPoint);

  return (
    <section
      data-cover-font={normalizedFont}
      data-gallery-state={state}
      data-gallery-style={normalizedStyle}
      style={{ ...coverFontVariables(normalizedFont), ...style }}
      className={`public-gallery-hero gallery-cover relative overflow-hidden bg-ink text-white ${className}`}
    >
      <div className="gallery-cover-media absolute inset-0">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="gallery-cover-image absolute inset-0 h-full w-full object-cover"
            style={focalStyle}
            onLoad={onImageLoad}
            onError={onImageError}
          />
        ) : (
          <div className="gallery-cover-fallback absolute inset-0" aria-hidden="true" />
        )}
        {video}
        <div className="gallery-cover-overlay absolute inset-0 transition-colors duration-300" />
      </div>

      <div className={`gallery-hero-composition relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center text-center ${
        unlocked ? 'gallery-hero-composition-unlocked' : ''
      }`}>
        <div className={`gallery-hero-copy ${unlocked ? 'gallery-hero-copy-unlocked' : ''}`}>
          <h1 className={`gallery-hero-title max-w-full break-words leading-none text-white ${
            unlocked ? 'text-5xl sm:text-7xl lg:text-8xl' : 'text-4xl sm:text-6xl lg:text-7xl'
          }`}>
            {title || 'Gallery'}
          </h1>
          {eventDate ? <p className="gallery-hero-date mt-4 text-sm font-medium text-white/85 sm:text-base">{formatDateOnlyLong(eventDate)}</p> : null}
          {description ? <p className="gallery-hero-description mt-5 text-base text-white/90 sm:mt-6 sm:text-lg">{description}</p> : null}
        </div>
        <div className="gallery-cover-action">{action}</div>
      </div>
      {controls}
    </section>
  );
}
